// SWARM INTELLIGENCE ENGINE — Slip Analysis (standalone product, SOT v1.0).
//
// Upload → vision OCR (swarm-extract) → confirm → fire 20 personas per leg
// (swarm-analyze-leg, one call/leg) → per-leg card with all 20 independent
// verdicts + consensus/dissent/risk + A20 signal. User drops legs to refine.

import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { getBrowserClient } from "../../supabase/browser.ts";
import { useSwarmHistory } from "./useSwarmHistory.ts";
import { SwarmHistory } from "./SwarmHistory.tsx";
import { trackEvent } from "../../conversion/trackEvent.ts";
import "../../styles/swarm.css";

type Phase = "idle" | "preview" | "extracting" | "confirming" | "analyzing" | "results" | "error";

interface SwarmLeg {
  id: string | null; leg_index: number; sport: string; home_team: string; away_team: string;
  league: string | null; country: string | null; market: string | null; selection: string | null; odds: number | null;
  needs_review?: boolean;
}
interface Verdict {
  agentId: string; agentName: string; chosenMarket: string | null; prediction: string | null;
  confidence: number; edge: number | null; rationale: string; dissent: boolean;
}
interface ModPick { market: string | null; selection: string | null; prediction: string | null; confidence: number; edge: number | null; rationale: string; }
interface ModeratorResult {
  value: ModPick | null; safe: ModPick | null; riskNote: string; scores: Record<string, number>;
}
interface LegResult {
  leg_id: string; data_status: string; resolution_note?: string | null; consensus_market: string | null; consensus_prediction: string | null;
  avg_confidence: number | null; dissent_count: number | null; risk_flag: string | null;
  a20_dissent: boolean; verdicts: Verdict[]; failed?: boolean;
  streaming?: boolean; done?: number; total?: number; moderating?: boolean; moderator?: ModeratorResult | null;
}

// Honest, human-readable explanation for a leg without a full fair price. Driven by
// the engine's resolution_note so a coverage gap ("no book odds") never reads the same
// as a match failure ("couldn't find this fixture") — and neither implies a bug.
function limitedReason(note: string | null | undefined): { label: string; tip: string } {
  switch (note) {
    case "no_markets":
    case "no_odds_for_fixture":
      return { label: "No book odds for this fixture", tip: "We found the fixture, but no bookmaker odds are available in our data feed yet — so there's no fair price to compute. Not all leagues/markets are priced by our providers." };
    case "fixture_not_found":
    case "no_fixture_match":
      return { label: "Couldn't match this fixture", tip: "We couldn't match these teams to a scheduled fixture in our data feed (often an unusual name spelling or a competition we don't cover). The agents still reasoned from available context." };
    case "team_not_found":
      return { label: "Couldn't identify a team", tip: "One of the team names couldn't be matched in our data feed. The agents still reasoned from available context." };
    case "non_football":
      return { label: "Limited model data", tip: "Full pricing data isn't available for this sport." };
    default:
      if (note && note.startsWith("budget")) return { label: "Temporarily unavailable", tip: "Live data fetch was rate-limited — re-run shortly." };
      return { label: "Limited data", tip: "Full pricing data wasn't available for this leg; confidence is capped accordingly." };
  }
}

// Direction of a pick for correlation analysis: low-scoring (Under / no-goals) bets
// are positively correlated — a single high-scoring slate busts them together.
function pickDirection(p: ModPick | null): "low" | "high" | "other" {
  if (!p) return "other";
  const s = `${p.selection ?? ""} ${p.prediction ?? ""} ${p.market ?? ""}`.toLowerCase();
  const btts = /btts|both teams/.test(s);
  if (/\bunder\b|not score|no goal|fewer/.test(s) || (btts && /\bno\b/.test(s))) return "low";
  if (/\bover\b|to score|\bmore\b/.test(s) || (btts && /\byes\b/.test(s))) return "high";
  return "other";
}

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";
const CONC = 5; // legs analysed in parallel
const SOFT_LIMIT = 20; // above this, ask the user to scope the run (cost/time guardrail)

const pretty = (s: string | null | undefined): string =>
  (s ?? "—").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const riskLabel = (r: string | null): string =>
  r === "high_confidence" ? "HIGH CONFIDENCE" : r === "high_risk" ? "HIGH RISK" : r === "unresolved" ? "UNRESOLVED" : "NEUTRAL";

function readAsDataUrl(f: File): Promise<string> {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = () => rej(new Error("Could not read the image.")); r.readAsDataURL(f); });
}

// ── Per-leg result card ──
function SwarmLegCard({ leg, result, dropped, onDrop, onRetry }: { leg: SwarmLeg; result?: LegResult; dropped: boolean; onDrop: () => void; onRetry: () => void }): JSX.Element {
  const [open, setOpen] = useState(false);
  const r = result;
  const streaming = r?.streaming === true;
  const showBody = !!r && (open || streaming);
  return (
    <div className={`swarm-leg${dropped ? " dropped" : ""}`}>
      <div className="swarm-leg-head" onClick={() => r && !streaming && !r.failed && setOpen((v) => !v)}>
        <span className="swarm-leg-match">{leg.home_team} vs {leg.away_team}</span>
        {streaming ? (
          <>
            <span className="swarm-consensus-badge">{r!.done ?? r!.verdicts.length}/{r!.total ?? 20} analysts</span>
            <div className="swarm-conf-bar"><span style={{ width: `${((r!.done ?? r!.verdicts.length) / (r!.total ?? 20)) * 100}%` }} /></div>
            <span className="swarm-confirm-ctx">{r!.moderating ? "moderator deciding…" : "firing…"}</span>
          </>
        ) : r?.failed ? (
          <button className="swarm-risk high_risk swarm-retry-btn" style={{ marginLeft: "auto" }} onClick={(e) => { e.stopPropagation(); onRetry(); }}>COULDN'T ANALYZE — TAP TO RETRY</button>
        ) : r ? (
          <>
            <span className="swarm-consensus-badge">{pretty(r.consensus_prediction)} · {pretty(r.consensus_market)}</span>
            <div className="swarm-conf-bar"><span style={{ width: `${r.avg_confidence ?? 0}%` }} /></div>
            <span className={`swarm-risk ${r.risk_flag ?? "neutral"}`}>{riskLabel(r.risk_flag)}</span>
            <Icon name="chev" size={14} />
          </>
        ) : (
          <span className="swarm-confirm-ctx">analyzing…</span>
        )}
      </div>

      {showBody && r && (
        <>
          {r.moderator && (
            <div className="swarm-moderator">
              <span className="swarm-moderator-tag">⚖ THE MODERATOR</span>
              <div className="swarm-moderator-picks">
                {r.moderator.value && (
                  <div className="swarm-mod-card value">
                    <div className="swarm-mod-card-head">
                      <span className="lbl">BEST VALUE</span>
                      {r.moderator.value.edge != null && <span className={`swarm-mod-edge ${r.moderator.value.edge >= 0 ? "pos" : "neg"}`}>{r.moderator.value.edge >= 0 ? "+" : ""}{r.moderator.value.edge.toFixed(1)}% edge</span>}
                      <span className="swarm-moderator-conf">{r.moderator.value.confidence}%</span>
                    </div>
                    <div className="swarm-moderator-pick">{pretty(r.moderator.value.prediction)}{r.moderator.value.market ? <span className="mkt"> · {pretty(r.moderator.value.market)}</span> : null}</div>
                    {r.moderator.value.rationale && <div className="swarm-moderator-why">{r.moderator.value.rationale}</div>}
                  </div>
                )}
                {r.moderator.safe && (
                  <div className="swarm-mod-card safe">
                    <div className="swarm-mod-card-head">
                      <span className="lbl">MOST LIKELY</span>
                      <span className="swarm-moderator-conf">{r.moderator.safe.confidence}%</span>
                    </div>
                    <div className="swarm-moderator-pick">{pretty(r.moderator.safe.prediction)}{r.moderator.safe.market ? <span className="mkt"> · {pretty(r.moderator.safe.market)}</span> : null}</div>
                    {r.moderator.safe.rationale && <div className="swarm-moderator-why">{r.moderator.safe.rationale}</div>}
                  </div>
                )}
              </div>
              {r.moderator.riskNote && <div className="swarm-moderator-risk">⚠ {r.moderator.riskNote}</div>}
            </div>
          )}
          <div className="swarm-verdicts">
            {r.verdicts.map((v) => {
              const score = r.moderator?.scores?.[v.agentId];
              return (
              <div key={v.agentId} className={`swarm-verdict-row${v.agentId === "A20" ? " a20" : v.dissent ? " dissent" : ""}`}>
                <span className="swarm-agent-name"><span className="id">{v.agentId}</span> {v.agentName}{typeof score === "number" ? <span className="swarm-mod-score" title="Moderator score">{score}</span> : null}</span>
                <span className="swarm-verdict-pick">{pretty(v.prediction)} <span style={{ color: "var(--sw-text-muted)", fontWeight: 400 }}>· {pretty(v.chosenMarket)}</span></span>
                <span className="swarm-verdict-conf">{v.confidence}%</span>
                <span className={`swarm-verdict-edge ${v.edge != null && v.edge > 0 ? "pos" : "neg"}`}>{v.edge != null ? `${v.edge > 0 ? "+" : ""}${v.edge.toFixed(1)}%` : "—"}</span>
                <span className="swarm-verdict-rationale">{v.rationale}</span>
              </div>
              );
            })}
          </div>
          {!streaming && (
            <div className="swarm-leg-summary">
              <span className="stat">Consensus <b>{pretty(r.consensus_prediction)}</b></span>
              <span className="stat">Avg confidence <b>{r.avg_confidence ?? "—"}</b></span>
              <span className="stat">Dissent <b>{r.dissent_count ?? "—"}/19</b></span>
              <span className="stat" style={{ color: r.a20_dissent ? "var(--sw-red)" : "var(--sw-green)" }}>A20 {r.a20_dissent ? "flags risk ⚠" : "aligned ✓"}</span>
              {(() => {
                const noOdds = r.resolution_note === "no_markets" || r.resolution_note === "no_odds_for_fixture";
                if (r.data_status === "enriched" && !noOdds) return null;
                const { label, tip } = limitedReason(r.resolution_note);
                return <span className="stat" style={{ color: "var(--sw-amber)", cursor: "help" }} title={tip}>{label}</span>;
              })()}
              <button className="swarm-drop-btn" onClick={(e) => { e.stopPropagation(); onDrop(); }}>{dropped ? "Dropped" : "Drop this leg"}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function SwarmPage(): JSX.Element {
  const { user } = useAuthSession();
  // Key effects off the STABLE user id, not the user object — Supabase swaps the
  // user object on every window-focus token refresh, which otherwise re-fires the
  // session loader and wipes a finished analysis.
  const userId = user?.id ?? null;
  const [phase, setPhase] = useState<Phase>("idle");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [legs, setLegs] = useState<SwarmLeg[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [bookmaker, setBookmaker] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, LegResult>>({});
  const [analyzed, setAnalyzed] = useState(0);
  const [dropped, setDropped] = useState<Set<string>>(new Set());
  const [runN, setRunN] = useState(SOFT_LIMIT); // how many legs to analyze on a big slip
  const [runTotal, setRunTotal] = useState(0); // legs actually being analyzed (progress denominator)
  const inputRef = useRef<HTMLInputElement>(null);
  const loadedSessionRef = useRef<string | null>(null); // session already loaded — never reload/reset it
  const history = useSwarmHistory(); // Slice D — recent analyses to resume
  const [sendingBack, setSendingBack] = useState(false); // Slice E — return to Extractor

  // Slice E — return circle: send the kept (non-dropped) legs back to the
  // Extractor to trim. Pulls the freshest swarm_legs (with the resolved
  // fixture_id, persisted post-analysis) so the Extractor reuses resolution AND
  // re-reads the swarm verdict for a swarm-informed cut.
  const sendToExtractor = useCallback(async (): Promise<void> => {
    if (sendingBack || !sessionId) return;
    setSendingBack(true);
    try {
      const client = getBrowserClient();
      const { data: legRows } = await client
        .from("swarm_legs" as never)
        .select("id,leg_index,home_team,away_team,league,sport,slip_market,slip_selection,slip_odds,fixture_id")
        .eq("session_id" as never, sessionId as never)
        .order("leg_index" as never);
      const kept = ((legRows ?? []) as Array<Record<string, unknown>>)
        .filter((r) => !dropped.has(String(r.id)))
        .map((r) => ({
          home_team: r.home_team, away_team: r.away_team, league: r.league,
          market: r.slip_market, selection: r.slip_selection, odds: r.slip_odds,
          fixture_id: (r.fixture_id as string | null) ?? null, sport: String(r.sport ?? "football"),
        }));
      if (kept.length === 0) { setSendingBack(false); return; }
      const { data, error: e } = await client.functions.invoke("slip-extract", {
        body: { action: "ingest_legs", source_hint: "Slip Analysis", legs: kept },
      });
      const eid = (data as { extraction_id?: string } | null)?.extraction_id;
      if (e || !eid) { setSendingBack(false); return; }
      window.location.assign(`/slips/extractor?extraction=${encodeURIComponent(eid)}`);
    } catch { setSendingBack(false); }
  }, [sendingBack, sessionId, dropped]);

  // Hand-off / resume: if the URL carries ?session=<id> — from the Extractor's
  // "Deep-analyze" button (Slice A) or the Analysis history (Slice D) — load that
  // session's legs and jump straight to the confirm step, skipping the upload.
  useEffect(() => {
    if (!userId) return;
    const sid = new URLSearchParams(window.location.search).get("session");
    if (!sid) return;
    // Load a session AT MOST ONCE. A window-focus token refresh re-runs this
    // effect; without the guard it would wipe a finished analysis and prompt a
    // costly re-run. (Fix: never reset; never re-read a slip we already have.)
    if (loadedSessionRef.current === sid) return;
    loadedSessionRef.current = sid;
    let cancelled = false;
    (async () => {
      setPhase("extracting");
      try {
        const client = getBrowserClient();
        const [legsRes, sessRes, verdRes] = await Promise.all([
          client.from("swarm_legs" as never)
            .select("id,leg_index,sport,home_team,away_team,league,country,slip_market,slip_selection,slip_odds,data_status,resolution_note,consensus_market,consensus_prediction,avg_confidence,dissent_count,risk_flag,a20_dissent,moderator_value,moderator_safe,moderator_risk_note,moderator_scores")
            .eq("session_id" as never, sid as never)
            .order("leg_index" as never),
          client.from("swarm_sessions" as never).select("bookmaker").eq("id" as never, sid as never).maybeSingle(),
          client.from("swarm_verdicts" as never)
            .select("leg_id,agent_id,agent_name,chosen_market,prediction,confidence,edge,rationale,dissent")
            .eq("session_id" as never, sid as never),
        ]);
        if (cancelled) return;
        const legRows = ((legsRes as { data?: unknown }).data ?? []) as Array<Record<string, unknown>>;
        const loaded = legRows.map((r) => ({
          id: (r.id as string) ?? null,
          leg_index: Number(r.leg_index ?? 0),
          sport: String(r.sport ?? "football"),
          home_team: String(r.home_team ?? ""),
          away_team: String(r.away_team ?? ""),
          league: (r.league as string | null) ?? null,
          country: (r.country as string | null) ?? null,
          market: (r.slip_market as string | null) ?? null,
          selection: (r.slip_selection as string | null) ?? null,
          odds: typeof r.slip_odds === "number" ? (r.slip_odds as number) : null,
        })) as SwarmLeg[];
        if (loaded.length === 0) { loadedSessionRef.current = null; setPhase("idle"); return; }
        setSessionId(sid);
        setLegs(loaded);
        setBookmaker(((sessRes as { data?: { bookmaker?: string | null } | null }).data ?? null)?.bookmaker ?? null);

        // RECORDED RESULTS: if this session was already analysed, rebuild the
        // results view from the stored verdicts — NO LLM, no re-analyse.
        const verdRows = ((verdRes as { data?: unknown }).data ?? []) as Array<Record<string, unknown>>;
        const analysedRows = legRows.filter((r) => r.consensus_prediction != null);
        if (analysedRows.length > 0) {
          const byLeg = new Map<string, Array<Record<string, unknown>>>();
          for (const v of verdRows) {
            const k = String(v.leg_id);
            (byLeg.get(k) ?? byLeg.set(k, []).get(k)!).push(v);
          }
          const built: Record<string, LegResult> = {};
          for (const r of legRows) {
            if (r.consensus_prediction == null) continue;
            const id = String(r.id);
            built[id] = {
              leg_id: id,
              data_status: String(r.data_status ?? ""),
              resolution_note: (r.resolution_note as string | null) ?? null,
              consensus_market: (r.consensus_market as string | null) ?? null,
              consensus_prediction: (r.consensus_prediction as string | null) ?? null,
              avg_confidence: (r.avg_confidence as number | null) ?? null,
              dissent_count: (r.dissent_count as number | null) ?? null,
              risk_flag: (r.risk_flag as string | null) ?? null,
              a20_dissent: r.a20_dissent === true,
              verdicts: (byLeg.get(id) ?? []).map((v) => ({
                agentId: String(v.agent_id ?? ""), agentName: String(v.agent_name ?? ""),
                chosenMarket: (v.chosen_market as string | null) ?? null,
                prediction: (v.prediction as string | null) ?? null,
                confidence: Number(v.confidence ?? 0), edge: (v.edge as number | null) ?? null,
                rationale: String(v.rationale ?? ""), dissent: v.dissent === true,
              })),
              moderator: (r.moderator_value || r.moderator_safe)
                ? { value: (r.moderator_value as ModeratorResult["value"]) ?? null, safe: (r.moderator_safe as ModeratorResult["safe"]) ?? null, riskNote: String(r.moderator_risk_note ?? ""), scores: (r.moderator_scores as Record<string, number>) ?? {} }
                : null,
            };
          }
          setResults(built);
          setRunTotal(analysedRows.length);
          setAnalyzed(analysedRows.length);
          setPhase("results"); // show the recorded analysis — never re-analyse
        } else {
          setPhase("confirming"); // not analysed yet — ready to run
        }
      } catch {
        if (!cancelled) { loadedSessionRef.current = null; setPhase("idle"); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Persist the active session into the URL once we have one (upload OR hand-off)
  // so a true reload restores the analysis instead of re-reading the slip. Marks
  // it loaded so the loader never re-fetches/resets the state we already hold.
  useEffect(() => {
    if (!sessionId || phase === "idle") return;
    loadedSessionRef.current = sessionId;
    const u = new URL(window.location.href);
    if (u.searchParams.get("session") !== sessionId) {
      u.searchParams.set("session", sessionId);
      window.history.replaceState(null, "", u.toString());
    }
  }, [sessionId, phase]);

  const onPick = useCallback((f: File | null) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { setError("Please upload an image (JPG, PNG, WEBP, HEIC)."); return; }
    setError(null); setFile(f);
    setPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return URL.createObjectURL(f); });
    setPhase("preview");
  }, []);

  const reset = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null); setPreviewUrl(null); setError(null); setLegs([]); setTruncated(false);
    setBookmaker(null); setResults({}); setAnalyzed(0); setDropped(new Set()); setSessionId(null); setPhase("idle");
    // Drop the persisted session so "Start over" / "Analyze another" is a clean
    // slate (and the loader won't restore the old session on the next render).
    loadedSessionRef.current = null;
    const u = new URL(window.location.href);
    if (u.searchParams.has("session")) { u.searchParams.delete("session"); window.history.replaceState(null, "", u.toString()); }
  }, [previewUrl]);

  const startExtraction = useCallback(async () => {
    if (!file) return;
    setPhase("extracting"); setError(null);
    trackEvent("swarm_slip_uploaded", { size_kb: Math.round(file.size / 1024), mime: file.type });
    try {
      const dataUrl = await readAsDataUrl(file);
      const client = getBrowserClient();
      const { data, error: e } = await client.functions.invoke("swarm-extract", { body: { image_base64: dataUrl, source_hint: "unknown" } });
      if (e) throw new Error(e.message);
      const d = (data ?? {}) as Record<string, unknown>;
      if (!d.ok) throw new Error(String(d.error ?? "Extraction failed."));
      setSessionId(typeof d.session_id === "string" ? d.session_id : null);
      setLegs(Array.isArray(d.legs) ? (d.legs as SwarmLeg[]) : []);
      setTruncated(d.truncated === true);
      setBookmaker(typeof d.bookmaker === "string" ? d.bookmaker : null);
      trackEvent("swarm_extraction_complete", {
        legs: Array.isArray(d.legs) ? d.legs.length : 0,
        truncated: d.truncated === true,
        bookmaker: typeof d.bookmaker === "string" ? d.bookmaker : null,
      });
      setPhase("confirming");
    } catch (err) { setError(err instanceof Error ? err.message : String(err)); setPhase("error"); }
  }, [file]);

  const editLeg = useCallback((i: number, patch: Partial<SwarmLeg>) =>
    setLegs((prev) => prev.map((l) => (l.leg_index === i ? { ...l, ...patch } : l))), []);

  const failLeg = useCallback((id: string) => setResults((prev) => ({ ...prev, [id]: {
    leg_id: id, data_status: "", consensus_market: null, consensus_prediction: null,
    avg_confidence: null, dissent_count: null, risk_flag: null, a20_dissent: false, verdicts: [], failed: true,
  } })), []);

  // Stream ONE leg's 20 verdicts (then moderator + aggregate) via SSE. Throws on
  // error so callers can mark the leg failed. Reusable by the full run and retry.
  const analyzeLeg = useCallback(async (id: string): Promise<void> => {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/swarm-analyze-leg`;
    const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
    const { data: sess } = await getBrowserClient().auth.getSession();
    const token = sess?.session?.access_token ?? anon;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "text/event-stream", "apikey": anon, "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ leg_id: id, stream: true }),
    });
    if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
    setResults((prev) => ({ ...prev, [id]: {
      leg_id: id, data_status: "", consensus_market: null, consensus_prediction: null,
      avg_confidence: null, dissent_count: null, risk_flag: null, a20_dissent: false,
      verdicts: [], streaming: true, done: 0, total: 20,
    } }));
    const reader = res.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    let gotDone = false;
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const parts = buf.split("\n\n");
      buf = parts.pop() ?? "";
      for (const block of parts) {
        const line = block.split("\n").find((l) => l.startsWith("data:"));
        if (!line) continue;
        let ev: Record<string, unknown>;
        try { ev = JSON.parse(line.slice(5).trim()); } catch { continue; }
        if (ev.type === "meta") {
          setResults((prev) => { const cur = prev[id]; if (!cur) return prev; return { ...prev, [id]: { ...cur, data_status: String(ev.data_status ?? ""), total: typeof ev.total === "number" ? ev.total : 20 } }; });
        } else if (ev.type === "verdict") {
          setResults((prev) => {
            const cur = prev[id]; if (!cur) return prev;
            return { ...prev, [id]: { ...cur, verdicts: [...cur.verdicts, ev.verdict as Verdict], done: typeof ev.done === "number" ? ev.done : cur.verdicts.length + 1 } };
          });
        } else if (ev.type === "moderating") {
          setResults((prev) => { const cur = prev[id]; if (!cur) return prev; return { ...prev, [id]: { ...cur, moderating: true } }; });
        } else if (ev.type === "done") {
          gotDone = true;
          setResults((prev) => ({ ...prev, [id]: {
            leg_id: id, data_status: String(ev.data_status ?? ""), resolution_note: (ev.resolution_note as string) ?? null,
            consensus_market: (ev.consensus_market as string) ?? null, consensus_prediction: (ev.consensus_prediction as string) ?? null,
            avg_confidence: typeof ev.avg_confidence === "number" ? ev.avg_confidence : null,
            dissent_count: typeof ev.dissent_count === "number" ? ev.dissent_count : null,
            risk_flag: (ev.risk_flag as string) ?? null, a20_dissent: ev.a20_dissent === true,
            verdicts: Array.isArray(ev.verdicts) ? (ev.verdicts as Verdict[]) : (prev[id]?.verdicts ?? []),
            streaming: false, moderating: false,
            moderator: (ev.moderator as ModeratorResult | null) ?? null,
          } }));
        } else if (ev.type === "error") {
          throw new Error(String(ev.error ?? "stream error"));
        }
      }
    }
    if (!gotDone) throw new Error("stream ended early");
  }, []);

  // Re-fire a single leg (e.g. after a budget/transient failure) without redoing the slip.
  const retryLeg = useCallback(async (id: string) => {
    try { await analyzeLeg(id); } catch { failLeg(id); }
  }, [analyzeLeg, failLeg]);

  // Fire the swarm per leg (bounded parallel), rendering cards as they land.
  const runSwarm = useCallback(async (limit?: number) => {
    // Never re-analyse (or re-spend on) a leg we already have results for — only
    // the legs the swarm hasn't worked on yet. The Slice C cache is the safety
    // net; this skips the redundant call entirely.
    const pending = legs.filter((l) => l.id && !results[l.id as string]);
    if (pending.length === 0) {
      if (Object.keys(results).length > 0) { setPhase("results"); return; } // all done — just show them
      setError("No legs to analyze."); setPhase("error"); return;
    }
    const targets = pending.slice(0, limit ?? undefined);
    setRunTotal(targets.length);
    setPhase("analyzing"); setAnalyzed(0); setError(null); // keep existing results; only add the new legs
    // Keep the session record in step with the run so it isn't stuck on
    // 'confirming' after a completed analysis (history/state read the status).
    const setSessionStatus = (status: string) => {
      if (!sessionId) return;
      void getBrowserClient().from("swarm_sessions" as never)
        .update({ status } as never).eq("id" as never, sessionId as never);
    };
    setSessionStatus("analyzing");

    const queue = [...targets];
    const worker = async () => {
      while (queue.length) {
        const leg = queue.shift(); if (!leg || !leg.id) continue;
        try { await analyzeLeg(leg.id); }
        catch { failLeg(leg.id as string); }
        finally { setAnalyzed((n) => n + 1); }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONC, targets.length) }, worker));
    trackEvent("swarm_analysis_complete", { legs: targets.length });
    setSessionStatus("complete");
    setPhase("results");
  }, [legs, analyzeLeg, failLeg, sessionId, results]);

  const dropLeg = useCallback(async (legId: string) => {
    const willDrop = !dropped.has(legId);
    setDropped((prev) => { const n = new Set(prev); willDrop ? n.add(legId) : n.delete(legId); return n; });
    if (willDrop) trackEvent("swarm_leg_dropped", { leg_id: legId });
    if (!user || !sessionId) return;
    try {
      const client = getBrowserClient() as unknown as {
        from: (t: string) => { upsert: (r: unknown, o: unknown) => Promise<unknown> };
      };
      await client.from("swarm_leg_actions")
        .upsert({ session_id: sessionId, leg_id: legId, user_id: user.id, action: willDrop ? "dropped" : "kept" },
          { onConflict: "leg_id,user_id" });
    } catch { /* best-effort */ }
  }, [dropped, user, sessionId]);

  const total = legs.filter((l) => l.id).length;

  return (
    <div className="fp">
      <div className="swarm-root">
        <header className="swarm-head">
          <h1 className="swarm-title"><span className="bolt"><Icon name="bolt" size={22} /></span> SWARM INTELLIGENCE ENGINE</h1>
          <p className="swarm-sub">Upload a slip. 20 analysts + a moderator. Every leg dissected.</p>
        </header>

        {!user && <div className="swarm-firing"><div className="swarm-firing-label">Sign in to analyze a slip.</div></div>}

        {user && phase === "idle" && (
          <div className={`swarm-upload${dragging ? " drag" : ""}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); onPick(e.dataTransfer.files?.[0] ?? null); }}>
            <div className="swarm-upload-icon"><Icon name="bolt" size={30} /></div>
            <div className="swarm-upload-title">Drop your slip here. 20 agents are ready.</div>
            <div className="swarm-upload-hint">Tap to upload · JPG, PNG, WEBP, HEIC · screenshot from any source</div>
            <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={(e) => onPick(e.target.files?.[0] ?? null)} />
          </div>
        )}

        {/* Slice D — Slip Analysis history: resume a past session (?session loader). */}
        {user && phase === "idle" && history.rows.length > 0 && (
          <SwarmHistory rows={history.rows} />
        )}

        {user && phase === "preview" && previewUrl && (
          <div className="swarm-preview">
            <img src={previewUrl} alt="slip preview" />
            <div className="swarm-preview-meta">
              <div className="swarm-confirm-match">{file?.name ?? "slip image"}</div>
              <div className="swarm-confirm-ctx">{file ? `${Math.round(file.size / 1024)} KB · ${file.type}` : ""}</div>
              <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                <button className="swarm-btn" onClick={() => void startExtraction()}><Icon name="bolt" size={14} /> Read slip</button>
                <button className="swarm-btn ghost" onClick={reset}>Choose another</button>
              </div>
            </div>
          </div>
        )}

        {user && phase === "extracting" && (
          <div className="swarm-firing"><div className="swarm-firing-label">Reading your slip…</div><div className="swarm-firing-bar"><span style={{ width: "40%" }} /></div></div>
        )}

        {user && phase === "confirming" && (
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
              <div className="swarm-confirm-match">{legs.length} leg{legs.length === 1 ? "" : "s"} read{bookmaker ? ` · ${bookmaker}` : ""}</div>
              <div className="swarm-confirm-ctx">Check the fixtures, fix any OCR slips, then run the swarm.</div>
            </div>
            {truncated && <div className="swarm-confirm-ctx" style={{ color: "var(--sw-amber)", marginBottom: 10 }}>More than 50 legs — analyzing the first 50.</div>}
            {legs.map((l) => (
              <div className="swarm-confirm-leg" key={l.leg_index} style={l.needs_review ? { borderColor: "var(--sw-amber)" } : undefined}>
                <span className="swarm-confirm-idx">{String(l.leg_index + 1).padStart(2, "0")}</span>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <input className="swarm-edit" value={l.home_team} onChange={(e) => editLeg(l.leg_index, { home_team: e.target.value })} placeholder="Home" />
                    <span style={{ color: "var(--sw-text-muted)" }}>vs</span>
                    <input className="swarm-edit" value={l.away_team} onChange={(e) => editLeg(l.leg_index, { away_team: e.target.value })} placeholder="Away" />
                    {l.needs_review && <span className="swarm-confirm-ctx" style={{ color: "var(--sw-amber)" }} title="The reader wasn't sure of this row — please check it">⚠ check</span>}
                  </div>
                  <div className="swarm-confirm-ctx">{[l.league, l.country, l.sport].filter(Boolean).join(" · ")}{l.market ? ` · ${l.market}${l.selection ? `: ${l.selection}` : ""}` : ""}</div>
                </div>
                <span className="swarm-confirm-ctx">{l.odds ? `@ ${l.odds.toFixed(2)}` : ""}</span>
              </div>
            ))}
            {(() => {
              const analyzable = legs.filter((l) => l.id).length;
              const big = analyzable > SOFT_LIMIT;
              const n = Math.max(1, Math.min(runN, analyzable));
              if (!big) {
                return (
                  <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                    <button className="swarm-btn" onClick={() => void runSwarm()}><Icon name="bolt" size={14} /> Run the swarm — 20 agents</button>
                    <button className="swarm-btn ghost" onClick={reset}>Start over</button>
                  </div>
                );
              }
              return (
                <div className="swarm-preview" style={{ marginTop: 14, flexDirection: "column", gap: 12 }}>
                  <div className="swarm-confirm-ctx" style={{ color: "var(--sw-amber)" }}>
                    Big slip — {analyzable} legs, each dissected by 20 analysts. A full run takes a few minutes. Analyze how many?
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <span className="swarm-confirm-ctx">First</span>
                    <input
                      className="swarm-edit" type="number" min={1} max={analyzable} value={n}
                      style={{ minWidth: 72 }}
                      onChange={(e) => setRunN(Math.max(1, Math.min(analyzable, Number(e.target.value) || 1)))}
                    />
                    <span className="swarm-confirm-ctx">of {analyzable} legs</span>
                    <button className="swarm-btn" onClick={() => void runSwarm(n)}><Icon name="bolt" size={14} /> Analyze first {n}</button>
                    <button className="swarm-btn ghost" onClick={() => void runSwarm()}>Analyze all {analyzable}</button>
                    <button className="swarm-btn ghost" onClick={reset}>Start over</button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {user && (phase === "analyzing" || phase === "results") && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {phase === "analyzing" && (
              <div className="swarm-firing">
                <div className="swarm-firing-count">{analyzed} / {runTotal}</div>
                <div className="swarm-firing-label">legs analyzed · 20 agents each</div>
                <div className="swarm-firing-bar"><span style={{ width: `${runTotal ? (analyzed / runTotal) * 100 : 0}%` }} /></div>
              </div>
            )}
            {phase === "results" && (() => {
              // Correlation flag: when the value picks concentrate in the low-scoring
              // direction, they're positively correlated — one high-scoring slate busts
              // them together. Per-leg value can be real; stacking it isn't diversified.
              const dirs = legs
                .filter((l) => l.id && !dropped.has(l.id as string))
                .map((l) => results[l.id as string]?.moderator?.value)
                .filter((p): p is ModPick => !!p)
                .map(pickDirection);
              const low = dirs.filter((d) => d === "low").length;
              const total = dirs.length;
              if (total >= 4 && low >= 3 && low / total >= 0.5) {
                return (
                  <div className="swarm-firing" style={{ borderColor: "var(--sw-amber)", background: "rgba(245,180,80,0.08)" }}>
                    <div className="swarm-firing-label" style={{ color: "var(--sw-amber)" }}>⚠ Correlation risk — {low} of {total} value picks are low-scoring (Unders / no-goals)</div>
                    <div className="swarm-confirm-ctx" style={{ marginTop: 4 }}>
                      These are positively correlated: a single high-scoring matchday can bust them together. Each leg's edge may be real, but stacking one direction concentrates risk instead of diversifying it — consider trimming or mixing in independent markets.
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            {legs.filter((l) => l.id).map((l) => (
              <SwarmLegCard key={l.id} leg={l} result={results[l.id as string]} dropped={dropped.has(l.id as string)} onDrop={() => void dropLeg(l.id as string)} onRetry={() => void retryLeg(l.id as string)} />
            ))}
            {phase === "results" && (
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button className="swarm-btn" onClick={() => void sendToExtractor()} disabled={sendingBack}>
                  <Icon name="bolt" size={14} /> {sendingBack ? "Sending…" : "Send to Extractor to trim →"}
                </button>
                <button className="swarm-btn ghost" onClick={reset}>Analyze another slip</button>
              </div>
            )}
          </div>
        )}

        {phase === "error" && error && (
          <div className="swarm-firing">
            <div className="swarm-firing-label" style={{ color: "var(--sw-red)" }}>Heads up</div>
            <div style={{ fontFamily: "var(--sw-font-body)", color: "var(--sw-text-sec)", margin: "8px 0 14px" }}>{error}</div>
            <button className="swarm-btn ghost" onClick={reset}>Start over</button>
          </div>
        )}
      </div>
    </div>
  );
}
